import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import {
  Layout,
  Code,
  Shield,
  Users,
  School,
  GraduationCap,
  Briefcase,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

  const KeyFeatures = [
    {
      title: "Student Information System",
      description:
        "Maintain complete student records, attendance, performance, and communication in one place.",
    },
    {
      title: "Staff & Faculty Management",
      description:
        "Track staff profiles, workload, schedules, leave, and department performance smoothly.",
    },
    {
      title: "Timetable & Academic Planning",
      description:
        "Auto-generate and manage timetables, schedules, exam calendars, and academic events.",
    },
    {
      title: "Fees & Accounts Module",
      description:
        "Automate billing, receipts, dues tracking, and financial reporting with accuracy.",
    },
    {
      title: "Exam & Results Management",
      description:
        "Create exams, upload marks, calculate grades, and publish results digitally.",
    },
    {
      title: "Parent & Student Portal",
      description:
        "Give real-time access to attendance, notices, assignments, and results through web/mobile.",
    },
  ];

  const features = [
    {
      title: "Unified Dashboard View",
      description:
        "Access academics, administration, finance, and reports in one clean interface.",
    },
    {
      title: "Zero Manual Hassle",
      description:
        "Automations reduce time spent on repetitive administrative tasks.",
    },
    {
      title: "Secure & Cloud-Ready",
      description:
        "Your data is backed up, encrypted, and accessible anywhere with role-based permissions.",
    },
    {
      title: "Customizable for Any Institution",
      description:
        "Works seamlessly for schools, junior colleges, and universities.",
    },
  ];

  const benefits = [
    {
      icon: Briefcase,
      role: "Management",
      benefit:
        "Real-time reports, cost savings, and improved administration efficiency.",
    },
    {
      icon: School,
      role: "Teachers",
      benefit: "Easy attendance, grading, scheduling, and communication tools.",
    },
    {
      icon: GraduationCap,
      role: "Students",
      benefit: "Access assignments, notes, results & updates instantly.",
    },
    {
      icon: Users,
      role: "Parents",
      benefit:
        "Stay informed about progress, attendance, and announcements anytime.",
    },
  ];

  const processSteps = [
    {
      number: "01",
      title: "Requirement Discussion & Data Collection",
      description: "We understand your workflow and gather the existing data.",
      icon: Users,
      imagePath: "./images/process.png",
    },
    {
      number: "02",
      title: "Setup & Custom Configuration",
      description:
        "Modules, menus, user roles are tailored to your institution’s structure.",
      icon: Layout,
      imagePath: "./images/process.png",
    },
    {
      number: "03",
      title: "Training & Live Demonstration",
      description:
        "Faculty and admin staff receive hands-on training and guided practice.",
      icon: Code,
      imagePath: "./images/process.png",
    },
    {
      number: "04",
      title: "Go-Live & Dedicated Support",
      description:
        "We monitor performance, solve issues, and support continuously.",
      icon: Shield,
      imagePath: "./images/process.png",
    },
  ];

  return (
    <section className="py-20 px-[5%] bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Key Features Section */}
        <div className="mb-16">
          <h2
            className="text-center text-gray-800 text-3xl md:text-5xl font-bold md:leading-relaxed mb-12"
            data-aos="fade-up"
          >
            Key <span className="text-[#0d6b68]">Features</span>
          </h2>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {KeyFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-r-4 border-l-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mb-24 mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-aos="fade-up">
          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="w-full"
          >
            <img
              src="./images/why-choose-us.jpg"
              alt="why choose us"
              className="w-full rounded-3xl object-cover"
            />
          </motion.div>

          {/* RIGHT LIST */}
          <div className="space-y-6" data-aos="fade-up">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="flex gap-4 items-start group"
              >
                <CheckCircle
                  className="text-[#0d6b68] mt-1 transition group-hover:scale-110"
                  size={26}
                />

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 group-hover:text-[#0d6b68] transition">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mb-24 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-aos="fade-up">
           <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="w-full"
          >
            <img
              src="./images/how-it-works.png"
              alt="How It Helps"
              className="w-full rounded-3xl object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="space-y-6" data-aos="fade-up">
              {benefits.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 bg-white p-4 rounded-full shadow-sm border-l-4 border-r-4 border border-[#0d6b68] hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <item.icon className="text-[#0d6b68]" size={28} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item.role}
                    </h3>
                    <p className="text-gray-700 text-sm mt-1">{item.benefit}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Header */}
        <div className="text-center mb-24" data-aos="fade-up">
          <h2 className="text-4xl text-gray-800 md:text-6xl font-semibold mb-6">
            Our <span className="text-[#0d6b68]">Process</span>
          </h2>
          <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
            We follow a structured, step-by-step approach to ensure smooth
            deployment with zero disruptions. From environment setup to final
            rollout, everything is optimized, tested, and delivered ready for
            real-world use.
          </p>
        </div>

        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
